"use client"

import * as React from "react"
import { format } from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { useActiveBranchTimezone } from "@/features/auth/hooks/use-active-branch-timezone"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { useFiscalCalendarQuery } from "@/features/finance/services/finance-close-trust-query"
import { flattenFiscalPeriods, resolveFiscalPeriodRange } from "@/features/finance/lib/finance-period"
import { PeriodPreset, isFiscalPeriodPreset, resolvePeriodRange } from "@/shared/lib/dates/period-range"

export type UseFinancePeriodResult = {
  preset: PeriodPreset
  setPreset: (preset: PeriodPreset) => void
  /** Range start, `YYYY-MM-DD` in the branch timezone (undefined only if a fiscal preset can't resolve). */
  from?: string
  /** Range end, `YYYY-MM-DD` in the branch timezone. */
  to?: string
  /** Whether the workspace has a fiscal calendar (drives whether fiscal presets are offered). */
  hasFiscalPeriods: boolean
}

/**
 * Standard finance reporting period. Owns the preset selection and resolves it — in the branch timezone,
 * fiscal-calendar aware — to date-only `from`/`to` bounds. Consumers receive the dates and stay unaware of
 * the preset math; the calculation lives in {@link resolvePeriodRange} / {@link resolveFiscalPeriodRange}.
 */
export function useFinancePeriod(options?: { defaultPreset?: PeriodPreset }): UseFinancePeriodResult {
  const timeZone = useActiveBranchTimezone()
  const { workspaceId, activeContext, scopeBranch, parishBranch } = useFinancePageState()
  // Fiscal presets resolve against the BRANCH's calendar (IBA-118), not the workspace's.
  const branchId =
    activeContext === "parish" ? parishBranch?.id ?? scopeBranch?.id ?? null : scopeBranch?.id ?? null
  const calendarQuery = useFiscalCalendarQuery(workspaceId ?? null, branchId)
  const [preset, setPreset] = React.useState<PeriodPreset>(options?.defaultPreset ?? PeriodPreset.MonthToDate)

  const calendar = calendarQuery.data
  const hasFiscalPeriods = flattenFiscalPeriods(calendar).length > 0

  const { from, to } = React.useMemo<{ from?: string; to?: string }>(() => {
    const zonedNow = toZonedTime(new Date(), timeZone)
    const todayIso = format(zonedNow, "yyyy-MM-dd")

    if (isFiscalPeriodPreset(preset)) {
      return (
        resolveFiscalPeriodRange(
          preset as PeriodPreset.CurrentFiscalPeriod | PeriodPreset.PreviousFiscalPeriod,
          todayIso,
          calendar,
        ) ?? {}
      )
    }

    const range = resolvePeriodRange(preset, { now: zonedNow, weekStartsOn: 1 })
    if (!range) return {}
    return { from: format(range.start, "yyyy-MM-dd"), to: format(range.end, "yyyy-MM-dd") }
  }, [preset, timeZone, calendar])

  return { preset, setPreset, from, to, hasFiscalPeriods }
}
